import { search } from "@/taskpane/lib/common/search";
import { DocumentParagraph, OfficeEnvironment } from "@/types/document";
import { diffWords } from "diff";
import { getWordDiff, reversibleHash } from "../utils/text-operators";
import { DOCUMENT_STORAGE_KEY, saveToDocumentStorage } from "./document-storage";
import OfficeSettings from "@/constants/office";
import { Performance } from "@/utils/performance";
import log from "../../../utils/logger";
import { applyTextChanges } from "./patch";

/* global Word */
/* global Office */
/* global OfficeExtension */

type UpdateFnReturn = { paragraphId: string; updatedText: string; success: boolean };

export async function insertText(text: string) {
  // Write text to the document.
  try {
    await Office.onReady();
    await Word.run(async (context) => {
      let body = context.document.body;
      body.insertParagraph(text, Word.InsertLocation.end);
      await context.sync();
    });
  } catch (error) {
    log.error("Text utility error", undefined, error);
  }
}

export async function highlightText(text: string, color = "yellow"): Promise<Word.Range | null> {
  try {
    await Office.onReady();
    return await Word.run(async (context) => {
      const body = context.document.body;
      const ranges = body.search(text, { matchCase: false, matchWholeWord: false });
      context.load(ranges, "items");

      await context.sync();

      if (ranges.items.length === 0) {
        return null;
      }

      ranges.items.forEach((range) => {
        range.font.highlightColor = color;
      });

      // Load the first range's start position
      const firstRange = ranges.items[0];
      firstRange.load("start");

      await context.sync();

      // Scroll to the first highlighted range
      firstRange.select();

      return firstRange;
    });
  } catch (error) {
    log.error("Text utility error", undefined, error);
    throw error;
  }
}

export async function removeHighlight(text: string): Promise<void> {
  try {
    await Office.onReady();
    await Word.run(async (context) => {
      const body = context.document.body;
      const ranges = body.search(text, { matchCase: false, matchWholeWord: false });
      context.load(ranges, "items");
      await context.sync();

      if (ranges.items.length === 0) {
        return;
      }

      ranges.items.forEach((range) => {
        range.font.highlightColor = null;
      });

      await context.sync();

      // Move the cursor to the end of the last modified range
      if (ranges.items.length > 0) {
        const lastRange = ranges.items[ranges.items.length - 1];
        lastRange.select("End");
      }

      await context.sync();
    });
  } catch (error) {
    log.error("Text utility error", undefined, error);
    throw error;
  }
}

export async function selectText(text: string): Promise<Word.Range | null> {
  try {
    await Office.onReady();
    return await Word.run(async (context) => {
      const body = context.document.body;
      const ranges = body.search(text, { matchCase: false, matchWholeWord: false });
      context.load(ranges, "items");

      await context.sync();

      if (ranges.items.length === 0) {
        return null;
      }

      ranges.items.forEach((range) => {
        range.select();
      });

      // Load the first range's start position
      const firstRange = ranges.items[0];
      firstRange.load("start");

      await context.sync();

      // Scroll to the first highlighted range
      firstRange.select();

      return firstRange;
    });
  } catch (error) {
    log.error("Text utility error", undefined, error);
    throw error;
  }
}

export async function removeTextSelection(text: string): Promise<void> {
  await Office.onReady();
  await Word.run(async (context) => {
    const body = context.document.body;
    const ranges = body.search(text, { matchCase: false, matchWholeWord: false });
    context.load(ranges, "items");
    await context.sync();

    ranges.items.forEach((range) => {
      range.select("End");
    });

    await context.sync();
  });
}

export async function selectByUniqueLocalId(id: string): Promise<void> {
  try {
    await Office.onReady();
    await Word.run(async (context) => {
      // Load the range
      const body = context.document.body;
      const paragraphs = body.paragraphs.load("items");

      context.load(paragraphs, "items");
      await context.sync();

      const paragraph = paragraphs.items.find((p) => p.uniqueLocalId === id);
      if (!paragraph) {
        return;
      }

      // Select the range
      paragraph.select();

      await context.sync();
    });
  } catch (error) {
    log.error("Text utility error", undefined, error);
    throw error;
  }
}

export async function removeSelectionByUniqueId(id: string): Promise<void> {
  try {
    await Office.onReady();
    await Word.run(async (context) => {
      // Load the range
      const body = context.document.body;
      const paragraphs = body.paragraphs.load("items");
      context.load(paragraphs, "items");
      await context.sync();

      const paragraph = paragraphs.items.find((p) => p.uniqueLocalId === id);
      if (!paragraph) {
        return;
      }

      // Move the selection to the end of the range
      paragraph.select("End");

      await context.sync();
    });
  } catch (error) {
    log.error("Text utility error", undefined, error);
    throw error;
  }
}

type DocumentParagraphLocalData = {
  paragraph: Word.Paragraph;
  currentParagraph: OfficeExtension.ClientResult<string>;
  originalParagraph: OfficeExtension.ClientResult<string>;
};

const loadListItem = async (context: Word.RequestContext, paragraphsLoadedData: Array<DocumentParagraphLocalData>) => {
  if (OfficeSettings.environment === OfficeEnvironment.Online) {
    // let hasListItem = false;
    // for (const paragraph of paragraphsLoadedData) {
    //   if (paragraph.paragraph.isListItem) {
    //     // Load the list item properties
    //     paragraph.paragraph.load("listItem/listString");
    //     hasListItem = true;
    //   }
    // }
    // if (hasListItem) {
    //   try {
    //     await context.sync();
    //   } catch (e) {
    //     // ignore error
    //     //this happens when the list item is to be deleted and changes were not yet confirmed
    //     // the paragraph is "considered" a list item, but has no list item.
    //     // this is a workaround to avoid the error
    //     // thanks word, you rock
    //   }
    // }
  } else {
    for (const paragraph of paragraphsLoadedData) {
      if (paragraph.paragraph.isListItem) {
        // Load the list item properties
        paragraph.paragraph.load("listItem/listString");
      }
    }
    await context.sync();
  }
};

const getListItemString = (paragraph: DocumentParagraphLocalData) => {
  if (OfficeSettings.environment === OfficeEnvironment.Desktop) {
    return paragraph.paragraph.isListItem ? paragraph.paragraph.listItem.listString : "";
  } else {
    return "";
    // try {
    //   return paragraph.paragraph.isListItem ? paragraph.paragraph.listItem.listString : "";
    // } catch (e) {
    //   // ignore error
    //   //this happens when the list item is to be deleted and changes were not yet confirmed
    //   // the paragraph is "considered" a list item, but has no list item.
    //   // this is a workaround to avoid the error
    //   // thanks word, you rock
    //   return "";
    // }
  }
};

/**
 * Retrieves all meaningful paragraphs from a Word document's body, excluding common separators,
 * whitespace-only entries, and non-content elements like page or section breaks.
 * It loads both the current and original versions of each paragraph to identify tracked changes,
 * cleans the content for processing, and assigns a unique ID to each paragraph.
 *
 * If no `RequestContext` is provided, it creates one using `Word.run`.
 *
 * The resulting paragraph data is also saved into document storage under a predefined key.
 *
 * @param {Word.RequestContext} [inputContext] - Optional Word request context. If not provided,
 * a new context is created with `Word.run`.
 *
 * @returns {Promise<Array<DocumentParagraph>>} A promise that resolves to an array of `DocumentParagraph` objects,
 * each representing a meaningful paragraph with metadata including:
 *  - `id`: A unique identifier (local ID or hashed),
 *  - `text`: The cleaned and meaningful paragraph text (excluding deletions),
 *  - `fullText`: The paragraph text with all tracked content (including insertions/deletions),
 *  - `_currentText` / `_originalText`: Raw text from Word's track changes,
 *  - `_index`: Index in the document order,
 *  - `_paragraph`: The original Word paragraph object.
 */
export async function getDocumentParagraphs(
  inputContext: Word.RequestContext | undefined = undefined
): Promise<Array<DocumentParagraph>> {
  const processId = Performance.startProcess("getDocumentParagraphs");
  const paragraphsLoadedData: Array<DocumentParagraphLocalData> = [];
  const paragraphInfo: Array<DocumentParagraph> = [];

  async function getParagraphs(context: Word.RequestContext) {
    const body = context.document.body;
    const paragraphs = body.paragraphs.load("items");
    await context.sync();

    const paramsToGet = ["uniqueLocalId", "text", "isListItem"];

    const items = paragraphs.items;
    for (const paragraph of items) {
      paragraph.load(paramsToGet);
    }
    await context.sync();

    for (const paragraph of items) {
      const currentParagraph = paragraph.getReviewedText("Current");
      const originalParagraph = paragraph.getReviewedText("Original");

      const item = {
        paragraph,
        currentParagraph,
        originalParagraph,
      };
      paragraphsLoadedData.push(item);
    }

    await context.sync();

    await loadListItem(context, paragraphsLoadedData);

    for (const paragraph of paragraphsLoadedData) {
      const currentText = paragraph.currentParagraph.value;
      const originalText = paragraph.originalParagraph.value;
      const diff = diffWords(originalText, currentText);

      let cleanText = "";
      diff.forEach((part) => {
        if (!part.removed) {
          cleanText += part.value;
        }
      });

      let numbering = "";
      if (paragraph.paragraph.isListItem) {
        numbering = getListItemString(paragraph) + "\t";
      }

      const fullText = `${numbering}${paragraph.paragraph.text}`;

      // only process the paragraph if it has text
      if (fullText !== "") {
        // Skip paragraphs that only contain whitespace characters, tabs, or common document separators
        const trimmedText = fullText.trim();

        // Create meaningful content detection patterns
        const containsAlphanumeric = /[a-zA-Z0-9]/.test(trimmedText);
        const containsMeaningfulPunctuation = /[.,;:!?()[\]{}""'']/.test(trimmedText);
        const isCommonSeparator = /^[-_*=]{3,}$/.test(trimmedText); // Matches common separators like "---", "___", "***", "==="
        const isPageBreak = /^(\[?page\s*break\]?)$/i.test(trimmedText);
        const isEmptyOrWhitespace = /^[\s\r\n\t]*$/.test(trimmedText); // Check for empty or whitespace-only, including \r\n
        const isWordSpecificBreak = /^(\r|\r\n|\n|(\[?(section|column)\s*break\]?))$/i.test(trimmedText); // Word-specific breaks

        // A paragraph is meaningful if it contains alphanumeric characters or meaningful punctuation
        // and is not just a common separator or page break or an empty line
        const hasMeaningfulContent =
          (containsAlphanumeric || containsMeaningfulPunctuation) &&
          !isCommonSeparator &&
          !isPageBreak &&
          !isEmptyOrWhitespace &&
          !isWordSpecificBreak;

        if (hasMeaningfulContent) {
          paragraphInfo.push({
            _paragraph: paragraph.paragraph,
            _index: paragraphInfo.length,
            _originalText: originalText,
            _currentText: currentText,
            id: paragraph.paragraph.uniqueLocalId || reversibleHash(originalText),
            hash: reversibleHash(originalText),
            text: numbering + cleanText,
            // full means that includes tracked text (both deleted and added)
            fullText,
          });
        }
      }
    }
  }

  if (!inputContext) {
    await Office.onReady();
    await Word.run(async (generatedContext) => {
      await getParagraphs(generatedContext);
    });
  } else {
    await getParagraphs(inputContext);
  }

  // Save the paragraph info to document storage
  saveToDocumentStorage(DOCUMENT_STORAGE_KEY.PARAGRAPHS, JSON.stringify(paragraphInfo));
  Performance.endProcess(processId, {
    paragraphsCount: paragraphInfo.length,
  });
  return paragraphInfo;
}

export async function highlight(range: Word.Range): Promise<boolean> {
  if (!range || range.isNullObject) {
    return false;
  }
  range.load("font");
  range.select();
  await range.context.sync();
  return true;
}

export async function searchAndHighlight(paragraph: DocumentParagraph): Promise<boolean> {
  await Office.onReady();
  return await Word.run(async (context) => {
    const searchResult = (
      await search({
        context,
        paragraph,
        options: {
          ignorePunct: true,
          ignoreSpace: true,
        },
        fallbackToParagraphWhenTextNotFound: true,
        fuzzySearch: true,
      })
    )?.range;

    if (!searchResult || searchResult?.isNullObject) {
      return false;
    }

    // Load any additional properties we need
    searchResult.load("font");
    searchResult.select();
    await context.sync();

    return true;
  });
}

export async function hasDeletedTrackedChanges({ range }: { range: Word.Range }): Promise<boolean> {
  if (!range || range.isNullObject) {
    return false;
  }

  const trackedChanges = range.getTrackedChanges();
  trackedChanges.load("items");
  await range.context.sync();

  return trackedChanges.items?.some((trackedChange) => trackedChange?.type === Word.TrackedChangeType.deleted);
}

export async function searchAndRemoveHighlight(inputParagraph: DocumentParagraph): Promise<void> {
  await Office.onReady();
  await Word.run(async (context) => {
    const searchResult = (
      await search({
        context,
        paragraph: inputParagraph,
        options: {
          ignorePunct: true,
          ignoreSpace: true,
        },
        textSearch: false,
      })
    )?.range;

    if (!searchResult || searchResult.isNullObject) {
      return;
    }

    searchResult.load("font");
    searchResult.select("End");
    await context.sync();
  });
}

const TEXT_RANGES_DELIMITERS = [
  " ", // Standard space
  "\u00A0", //	Non-breaking space (NBSP)	Prevents word breaks
  "\u200B", //	Zero-width space	Invisible but affects formatting
  "\u2002", //	En space (width of 'N')	Used for fixed spacing
  "\u2003", //	Em space (width of 'M')	Larger than en space
  "\u2004", //	Three-per-em space	1/3 of an em
  "\u2005", //	Four-per-em space	1/4 of an em
  "\u2006", //	Six-per-em space	1/6 of an em
  "\u2007", //	Figure space	Width of a digit
  "\u2008", //	Punctuation space	Width of a period
  "\u2009", //	Thin space	Narrower than standard space
  "\u200A", //	Hair space	Thinnest space
  "\u202F", //	Narrow no-break space	Like NBSP but narrower
  "\u205F", //	Medium mathematical space	Used in equations
  "\u3000", //	Ideographic space	Used in CJK texts
];

/**
 * Applies a word diff to a specific paragraph in a Word document.
 *
 * @param {object} params - Parameters for applying the word diff.
 * @param {Word.RequestContext} params.context - The Word request context used for batching operations.
 * @param {Word.Range} [params.range] - Optional precomputed range result from an earlier search. If not provided, a new search is performed.
 * @param {DocumentParagraph} params.paragraph - The paragraph object in which the diff should be applied.
 * @param {string} params.paragraphId - The unique identifier for the paragraph.
 * @param {string} params.sentence - The original sentence to match and locate within the paragraph.
 * @param {string} params.replaceWith - The new sentence that should replace differing parts of the original.
 *
 * @returns {Promise<{ paragraphId: string; updatedText: string; success: boolean }>} - A promise resolving to an object containing:
 *  - `paragraphId`: the ID of the updated paragraph,
 *  - `updatedText`: the replacement sentence,
 *  - `success`: true if the diff was applied successfully, false otherwise.
 */
export async function applyWordDiff({
  context,
  range,
  paragraph: inputParagraph,
  paragraphId,
  sentence,
  replaceWith,
}: {
  context: Word.RequestContext;
  range: Word.Range;
  paragraph: DocumentParagraph;
  paragraphId: string;
  sentence: string;
  replaceWith: string;
}): Promise<UpdateFnReturn> {
  // If the original sentence and the replacement are the same, there's nothing to do.
  if (sentence === replaceWith) {
    return { paragraphId, updatedText: "", success: true };
  }

  // 1. Use the search result passed on (it was done during highlighting if available).
  // If not, search for the paragraph
  const paragraph =
    range ||
    (
      await search({
        context,
        paragraph: inputParagraph,
        paragraphId,
        text: sentence,
        options: {
          ignorePunct: true,
          ignoreSpace: true,
        },
      })
    ).range;

  if (!paragraph || paragraph.isNullObject) {
    return { paragraphId, updatedText: "", success: false };
  }

  paragraph.load("font");
  context.document.load("changeTrackingMode");
  await context.sync();

  // 2. Prepare for changes: Temporarily enable tracking to prevent not tracking changes.
  const initialTrackingMode = context.document.changeTrackingMode;
  if (initialTrackingMode !== Word.ChangeTrackingMode.trackAll) {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  }

  await applyTextChanges(paragraph, replaceWith);

  context.document.changeTrackingMode = initialTrackingMode;
  await context.sync(); // Batch sync for all changes and property loads

  return { paragraphId, updatedText: replaceWith, success: true };
}

export async function getSelectedText() {
  await Office.onReady();
  return await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const currentSelection = selection.getReviewedText("Current");
    const originalSelection = selection.getReviewedText("Original");
    const selectionParagraph = selection.paragraphs.getFirst();
    selectionParagraph.load("uniqueLocalId");
    await context.sync();

    const currentText = currentSelection.value;
    const originalText = originalSelection.value;
    const diff = diffWords(originalText, currentText);
    let cleanText = "";
    diff.forEach((part) => {
      if (!part.removed) {
        cleanText += part.value;
      }
    });

    return { text: cleanText, paragraphId: selectionParagraph.uniqueLocalId };
  });
}

export async function getDocumentText() {
  await Office.onReady();
  return await Word.run(async (context) => {
    const currentTextResult = context.document.body.getReviewedText("Current");
    const originalTextResult = context.document.body.getReviewedText("Original");
    await context.sync();

    const currentText = currentTextResult.value;
    const originalText = originalTextResult.value;
    const diff = diffWords(originalText, currentText);

    let cleanText = "";
    diff.forEach((part) => {
      if (!part.removed) {
        cleanText += part.value;
      }
    });

    return cleanText;
  });
}

/**
 * Replaces the current text selection in a Word document with the provided text,
 * using tracked changes (via `applyWordDiff`) to preserve change history.
 *
 * @param {string} text - The text to insert at the user's current cursor selection.
 *
 * @returns {Promise<{ paragraphId: string; updatedText: string; success: boolean }>} - A promise resolving to an object containing:
 *  - `paragraphId`: the ID of the updated paragraph,
 *  - `updatedText`: the replacement sentence,
 *  - `success`: true if the diff was applied successfully, false otherwise.
 */
export async function insertTextAtCursor(text: string) {
  await Office.onReady();
  return await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load(["text", "isNullObject"]);
    await context.sync();

    if (!selection || selection.isNullObject) {
      return { paragraphId: "", updatedText: "", success: false };
    }
    return await applyWordDiff({
      context,
      range: selection,
      paragraph: null, // Not needed because the target paragraph is the user's selection
      paragraphId: "", // Not needed because the target paragraph is the user's selection
      sentence: selection.text,
      replaceWith: text,
    });
  });
}

/**
 * Inserts a block of text at the beginning of a sentence within a specified paragraph in a Word document.
 * If `range` is not provided, the sentence is located using a search. The method ensures the inserted
 * text is tracked using Word's change tracking mode and ends with a new line if one is not present.
 *
 * @param {object} params - Parameters for the text insertion.
 * @param {Word.RequestContext} params.context - The Word request context used to queue and synchronize actions.
 * @param {Word.Range} [params.range] - Optional precomputed range pointing to the target sentence. If omitted, a search is performed.
 * @param {DocumentParagraph} params.paragraph - The paragraph object that contains the sentence.
 * @param {string} params.paragraphId - A unique identifier for the paragraph.
 * @param {string} params.sentence - The sentence used to locate the insertion point within the paragraph.
 * @param {string} params.textToInsert - The text to insert before the sentence. A new line is appended if missing.
 *
 * @returns {Promise<{ paragraphId: string; updatedText: string; success: boolean }>} - A promise resolving to:
 *  - `paragraphId`: the ID of the updated or newly inserted paragraph (generated if missing),
 *  - `updatedText`: the inserted text,
 *  - `success`: true if insertion succeeded, false otherwise.
 */
export async function insertTextBeforeSentence({
  context,
  range,
  paragraph,
  paragraphId,
  sentence,
  textToInsert,
}: {
  context: Word.RequestContext;
  range: Word.Range;
  paragraph: DocumentParagraph;
  paragraphId: string;
  sentence: string;
  textToInsert: string;
}): Promise<UpdateFnReturn> {
  if (!textToInsert.length) {
    return { paragraphId, updatedText: "", success: false };
  }
  const searchResults =
    range ??
    (
      await search({
        context,
        paragraph,
        paragraphId,
        text: sentence,
        options: {
          ignorePunct: true,
          ignoreSpace: true,
        },
      })
    ).range;

  if (!searchResults || searchResults.isNullObject) {
    return { paragraphId, updatedText: "", success: false };
  }

  const shouldAddNewLine = !textToInsert.endsWith("\n") && !textToInsert.endsWith("\r");
  const normalizedText = `${textToInsert}${shouldAddNewLine ? "\r" : ""}`;

  context.document.load("changeTrackingMode");
  await context.sync();
  const initialTrackingMode = context.document.changeTrackingMode;

  // set the tracking mode to track all
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

  const insertedRange = searchResults.insertText(normalizedText, Word.InsertLocation.before);
  const insertedParagraph = insertedRange.paragraphs.getFirst();

  // restore initial tracking mode
  context.document.changeTrackingMode = initialTrackingMode;

  insertedParagraph.load(["uniqueLocalId", "text"]);
  await context.sync();

  return {
    paragraphId: insertedParagraph.uniqueLocalId || reversibleHash(insertedParagraph.text),
    updatedText: textToInsert,
    success: true,
  };
}
